import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import PaperPlaneIcon from 'icon/paperplane.svg';
import ArrowTriangleDownFillIcon from 'icon/arrow_triangle_down_fill.svg';
import { Avatar, Button, ButtonSize } from '@lemon8/design';
import { useAtomValue, useSetAtom } from 'jotai';
import { DivisibleStack, ELEMENT_TIMING_FMP } from '@lemon8/web-app-shared/component';
import { useNumFormatter } from '@lemon8/web-app-shared/hook';
import { useTranslation } from 'react-i18next';
import { $CurrentUserAsAvailable } from '@user/shared/current-user';
import { EPrivacyStatus, EUserGender } from 'shared/models';
import { useCTADirectly } from '~/utils/cta';
import { EClickPosition } from 'shared/constant';
import { activeToUser } from '~/utils/active/active-to-user';
import LikeCountConfirm from '@user/body/like-count-confirm';
import { UserLinks } from '@user/body/user-links';
import { $SimpleToastStatus } from '~/components/simple-toast/atom';
import { useViewTracking } from '~/utils/view-tracking';
import { sendCTAShowEvent } from '~/utils/event';
import { EPageModule } from 'shared/constant/page-module';

import './style.scss';

export const UserInfo: FC = () => {
  const { t } = useTranslation();
  const formatNumber = useNumFormatter();

  const user = useAtomValue($CurrentUserAsAvailable);
  const setSimpleToast = useSetAtom($SimpleToastStatus);

  const followingObservedRef = useViewTracking({
    onView: () =>
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.UserFollowing,
      }),
  });
  const handleFollowingCTA = useCTADirectly({
    clickPosition: EClickPosition.UserFollowing,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.UserFollowing);
    }, []),
  });

  const followerObservedRef = useViewTracking({
    onView: () =>
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.UserFans,
      }),
  });
  const handleFollowerCTA = useCTADirectly({
    clickPosition: EClickPosition.UserFans,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.UserFans);
    }, []),
  });

  const handleFollowingOrFansClick = useCallback(
    (position: EClickPosition.UserFollowing | EClickPosition.UserFans) => (e: SyntheticEvent) => {
      if (user.privacyStatus === EPrivacyStatus.PrivateUser) {
        setSimpleToast({
          content: t('privateAccount_profile_toast'),
        });
        return;
      }

      if (position === EClickPosition.UserFollowing) {
        handleFollowingCTA(e);
      } else {
        handleFollowerCTA(e);
      }
    },
    [user, handleFollowerCTA, handleFollowingCTA],
  );

  const likeConfirmObservedRef = useViewTracking({
    onView: () =>
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.UserLikes,
      }),
  });
  const handleLikeConfirmCTA = useCTADirectly({
    clickPosition: EClickPosition.UserLikes,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.UserLikes);
    }, []),
  });
  const handleLikeConfirmClick = useCallback(() => {
    if (user.privacyStatus === EPrivacyStatus.PrivateUser) {
      setSimpleToast({
        content: t('privateAccount_profile_toast'),
      });
      return;
    }

    handleLikeConfirmCTA();
  }, [user, handleLikeConfirmCTA]);

  const handleAuthorFollowButtonClick = useCTADirectly({
    clickPosition: EClickPosition.AuthorFollowButton,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.AuthorFollowButton);
    }, []),
  });

  const handleAuthorPrivateLetterButtonClick = useCTADirectly({
    clickPosition: EClickPosition.AuthorPrivateLetter,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.AuthorPrivateLetter);
    }, []),
  });

  const authorBtnGroupObservedRef = useViewTracking({
    onView: () => {
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.AuthorFollowButton,
      });
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.AuthorPrivateLetter,
      });
      sendCTAShowEvent({
        pageModule: EPageModule.AuthorInfo,
        currentPosition: EClickPosition.AuthorTriangularArrow,
      });
    },
  });
  const handleAuthorTriangularArrowButtonClick = useCTADirectly({
    clickPosition: EClickPosition.AuthorTriangularArrow,
    onClick: useCallback(() => {
      activeToUser(user, EClickPosition.AuthorTriangularArrow);
    }, []),
  });

  const genderValue = useMemo(() => {
    const { gender } = user;
    let res = '';
    if (gender === EUserGender.MALE) {
      res = t('male');
    } else if (gender === EUserGender.FEMALE) {
      res = t('female');
    } else if (gender === EUserGender.OTHER) {
      res = t('gender_other');
    }
    return res;
  }, [user]);

  const ageValue = useMemo(() => {
    const { ageGroup } = user;
    const AgeGroup = [
      { label: t('age_20_less'), value: 10 },
      { label: t('age_20'), value: 20 },
      { label: t('age_30'), value: 30 },
      { label: t('age_40'), value: 40 },
      { label: t('age_50'), value: 50 },
      { label: t('age_60_more'), value: 60 },
    ];
    return AgeGroup.find((item) => item.value === ageGroup)?.label;
  }, [user]);

  const locationValue = user.location;

  const showLink = useMemo(() => {
    const { thirdLinks } = user;
    return Boolean(thirdLinks.length);
  }, [user]);
  return (
    <section className="user-desc">
      <div className="user-desc-main">
        <Avatar className="user-desc-main-avatar" src={user.avatar} size={88} />
        <DivisibleStack wrapper="user-desc-main-info" divider="user-desc-main-info-divider">
          <div
            className="user-desc-main-info-item"
            onClick={handleFollowingOrFansClick(EClickPosition.UserFollowing)}
            ref={followingObservedRef}
          >
            <span elementtiming={ELEMENT_TIMING_FMP}>{formatNumber(user.followingCount)}</span>
            <span>{t('follow_count_number')}</span>
          </div>
          <div
            className="user-desc-main-info-item"
            onClick={handleFollowingOrFansClick(EClickPosition.UserFans)}
            ref={followerObservedRef}
          >
            <span>{formatNumber(user.followerCount)}</span>
            <span>{t('fans_count_number')}</span>
          </div>
          <div className="user-desc-main-info-item" onClick={handleLikeConfirmClick} ref={likeConfirmObservedRef}>
            <span>{formatNumber(user.dugCount + user.favoredCount)}</span>
            <span>{t('profile_likes_favors_count')}</span>
          </div>
        </DivisibleStack>
      </div>

      <div className="user-desc-base">
        <p className="user-desc-base-name">{user.nickName}</p>
        {user.badgeInfo?.icon_url && (
          <div style={{}}>
            <img src={user.badgeInfo.icon_url} alt="badge" style={{ height: 33, width: 'auto' }} />
          </div>
        )}
        {(genderValue || ageValue || locationValue || showLink) && (
          <DivisibleStack wrapper="user-desc-base-info" divider="user-desc-base-info-divider">
            {genderValue && <span>{genderValue}</span>}
            {ageValue && <span>{ageValue}</span>}
            {locationValue && <span className="location">{locationValue}</span>}
            {showLink && <UserLinks />}
          </DivisibleStack>
        )}
        {user.description && <p className="user-desc-base-desc">{user.description}</p>}
      </div>

      <div className="user-desc-action" ref={authorBtnGroupObservedRef}>
        <Button
          className="user-desc-action-follow"
          variant={'Primary'}
          size={ButtonSize.large}
          title={t('follow')}
          onClick={handleAuthorFollowButtonClick}
        />
        <Button
          className="user-desc-action-im"
          variant={'Secondary'}
          size={ButtonSize.large}
          icon={<PaperPlaneIcon />}
          onClick={handleAuthorPrivateLetterButtonClick}
        />
        <Button
          className="user-desc-action-more"
          variant={'Secondary'}
          size={ButtonSize.large}
          icon={<ArrowTriangleDownFillIcon />}
          onClick={handleAuthorTriangularArrowButtonClick}
        />
      </div>
      <LikeCountConfirm />
    </section>
  );
};
